import React, { useMemo, useState } from 'react'
import { FilterInput } from '@nyos/shared'
import type { Track } from '@nyos/shared'

/**
 * TrackIndex — every routable track as a compact row: name, type,
 * live car count, utilization bar. Clicking a row loads that track's
 * strip in the workspace. Tracks render in the API's yard order;
 * empties are dimmed but remain selectable.
 */

interface TrackIndexProps {
  tracks: Track[]
  selectedId: string
  onSelect: (trackId: string) => void
}

function UtilBar({ pct }: { pct: number | undefined }) {
  const v = Math.max(0, Math.min(100, Math.round((pct ?? 0) * (pct != null && pct <= 1 ? 100 : 1))))
  return (
    <div className="h-1.5 rounded-full bg-muted overflow-hidden" title={`${v}% utilized`}>
      <div
        className={`h-full rounded-full ${v > 90 ? 'bg-destructive' : v > 70 ? 'bg-amber-500' : 'bg-switch-normal'}`}
        style={{ width: `${v}%` }}
      />
    </div>
  )
}

const TrackIndex: React.FC<TrackIndexProps> = ({ tracks, selectedId, onSelect }) => {
  // Client-side only — the routable list is already cached; this filter
  // never re-fetches.
  const [query, setQuery] = useState('')
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return tracks
    return tracks.filter(
      (t) =>
        t.name.toLowerCase().includes(q) || (t.trackType ?? '').toLowerCase().includes(q)
    )
  }, [tracks, query])

  return (
    <div className="flex flex-col h-full rounded-lg border border-subtle bg-card overflow-hidden">
      <div className="px-3 py-2 border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Tracks <span className="font-normal">({tracks.length})</span>
      </div>
      <div className="px-2 py-1.5 border-b border-border">
        <FilterInput
          aria-label="Filter tracks"
          placeholder="Filter tracks…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-border">
      {visible.length === 0 && (
        <div className="p-3 text-xs text-muted-foreground">
          {tracks.length === 0 ? 'No routable tracks for this yard.' : 'No tracks match the filter.'}
        </div>
      )}
      {visible.map((t) => {
        const cars = t.currentCarCount ?? 0
        const selected = t.id === selectedId
        return (
          <button
            key={t.id}
            onClick={() => onSelect(t.id)}
            className={`w-full text-left px-3 py-2 transition-colors ${
              selected ? 'bg-primary/15 border-l-2 border-primary' : 'hover:bg-muted/50'
            } ${cars === 0 ? 'opacity-55' : ''}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-foreground truncate">{t.name}</span>
              <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                {cars} car{cars === 1 ? '' : 's'}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <UtilBar pct={t.currentUtilization} />
              {t.trackType && (
                <span className="text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">
                  {t.trackType}
                </span>
              )}
            </div>
          </button>
        )
      })}
      </div>
    </div>
  )
}

export default React.memo(TrackIndex)
